<script setup>
/*==================================================================*/
/*================== VUE IMPORTS ===================================*/
/*==================================================================*/

import { ref, onMounted, watch, computed } from 'vue';

/*==================================================================*/
/*================== IMPORTS DE COMPONENTES ========================*/
/*==================================================================*/

import TelaTabela from '@/components/v2/telas/TelaTabela/TelaTabela.vue';
import BotaoIconeBase from '@/components/v2/base/BotaoIconeBase/BotaoIconeBase.vue';
import CampoCheckboxBase from '@/components/v2/base/CampoCheckboxBase/CampoCheckboxBase.vue';
import VisualizarPedidoDeCompraModal from './VisualizarPedidoDeCompra/VisualizarPedidoDeCompraModal.vue';

/*==================================================================*/
/*================== IMPORTS DE API E UTILS ========================*/
/*==================================================================*/

import ERP from '../../api/v2/ERP.mjs';
import View from '../../api/v2/View.mjs';
import UtilPDF from '../../utils/UtilPDF';
import UtilArray from '@/utils/UtilArray.mjs';
import UtilSort from '@/utils/UtilSort.js';

/*==================================================================*/
/*=================== IMPORTS DO USUARIO ===========================*/
/*==================================================================*/

import useUsuarioStore from '@/api/v2/stores/UsuarioStore.js';
const usuarioStore = useUsuarioStore();

/*==================================================================*/
/*================= IMPORTS DO COLABORADOR =========================*/
/*==================================================================*/

import useColaboradorStore from '@/api/v2/stores/ColaboradorStore.js';
const colaboradorStore = useColaboradorStore();

/*==================================================================*/
/*======================== CONTROLES DE TELA =======================*/
/*==================================================================*/

const pedidoDeCompraLoading = ref(true);

/*==================================================================*/
/*====================== Visualizando Pedido =======================*/
/*==================================================================*/

const visualizandoPedido = ref(false);
const pedidoDeCompra = ref({});

/*==================================================================*/
/*======================== Pedido de Compra ========================*/
/*==================================================================*/

const pedidosDeCompra = ref([]);

/*==================================================================*/
/*======================== FILTROS DE BUSCA ========================*/
/*==================================================================*/

const buscaNumeroPedidoSelecionado = ref('');
const buscaFornecedorSelecionado = ref('');
const buscaJobSelecionado = ref('');
const buscaOSSelecionado = ref('');
const buscaOSItemItemCodigoSelecionado = ref('');
const buscaTemFollowUpsSelecionado = ref(false);

/*==================================================================*/
/*========================== HEADERS ===============================*/
/*==================================================================*/

const pedidoDeCompraHeaders = [
    { text: 'N° do Pedido', value: 'codigo', type: 'string' },
    { text: 'Status do Pedido', value: 'status', type: 'string' },
    { text: 'Situação do Recebimento', value: 'recebimento', type: 'string' },
    { text: 'Fornecedor', value: 'fornecedor.nome', type: 'string' },
    { text: 'Data Criação (Fornecedor)', value: 'dataCriacao', type: 'date' },
    { text: 'Vendedor(a)', value: 'fornecedorVendedor.nome', type: 'string' },
    { text: 'Comprador(a)', value: 'comprador.nome', type: 'string' },
    { text: 'Total dos itens', value: 'precoTotalComImpostos', type: 'money' },
    { text: 'Total Geral (Impostos Inclusos)', value: 'precoTotalComImpostosComIPI', type: 'money' },
    { text: 'Total dos Itens (Sem Impostos + IPI)', value: 'precoTotalSemImpostosComIPI', type: 'money' },
    { text: 'Total do Disponivel', value: 'valorDisponivel', type: 'money' },
    { text: 'Tem FollowUps?', value: 'temFollowUpsFormatado', type: 'string' },
    { text: 'Próxima Entrega', value: 'itensPrimeiraDataEntrega', type: 'date' },
    { text: 'Ultima Entrega', value: 'itensUltimaDataEntrega', type: 'date' },
    { text: 'Itens', value: 'itens', type: 'number' },
    { text: 'Parcelas', value: 'parcelas', type: 'number' },
    { text: 'Coordenadores', value: 'coordenadores', type: 'string' },
    { text: 'Diretor', value: 'diretor', type: 'string' },
];

/*==================================================================*/
/*================== ALTERA VISUALIZANDO PEDIDO ====================*/
/*==================================================================*/

const alteraVisualizandoPedido = (update = {}) => {

    visualizandoPedido.value = update?.visualizandoPedido ?? false;
    pedidoDeCompra.value = update?.pedidoDeCompra ?? {};

};

/*==================================================================*/
/*====================== ITENS DOS FILTROS =========================*/
/*==================================================================*/

const buscaNumerosPedidos = computed(() => {

    return pedidosDeCompra.value ?? [];

});

const buscaFornecedores = computed(() => {

    const fornecedores = pedidosDeCompra.value?.map((pedido = {}) => {
        return pedido?.fornecedor ?? null;
    }) ?? [];

    return fornecedores.filter((fornecedor) => {
        return fornecedor?.nome ? true : false;
    }).sort((fornecedorA = {}, fornecedorB = {}) => {

        const nomeA = fornecedorA?.nome ?? '';
        const nomeB = fornecedorB?.nome ?? '';

        return nomeA.localeCompare(nomeB, 'pt-BR', { sensitivity: 'base' });

    });

});

const itensDosPedidosDeCompra = computed(() => {

    return pedidosDeCompra.value?.reduce((acc, pedido = {}) => {

        const itens = pedido?.itens ?? [];
        acc.push(...itens);

        return acc;

    }, []) ?? [];

});

const buscaJobs = computed(() => {

    let jobs = UtilArray.uniqueBy(itensDosPedidosDeCompra.value, 'jobCodigo');
    jobs = UtilSort.descendingBy(jobs, 'jobCodigo');

    return jobs;

});

const buscaOS = computed(() => {

    let ordensDeServico = UtilArray.uniqueBy(itensDosPedidosDeCompra.value, 'osCodigo');
    ordensDeServico = UtilSort.descendingBy(ordensDeServico, 'osCodigo');

    return ordensDeServico;

});

const buscaCodigosDeItem = computed(() => {

    let codigos = UtilArray.uniqueBy(itensDosPedidosDeCompra.value, 'osItemItemCodigo');
    codigos = UtilSort.descendingBy(codigos, 'osItemItemCodigo');

    return codigos;

});

/*==================================================================*/
/*====================== ALTERA FILTROS ============================*/
/*==================================================================*/

const alteraBuscaNumeroPedidoSelecionado = (valor = {}) => {

    buscaNumeroPedidoSelecionado.value = valor?.codigo ?? '';

};

const alteraBuscaFornecedorSelecionado = (valor = {}) => {

    buscaFornecedorSelecionado.value = valor?.nome ?? '';

};

const alteraBuscaJobSelecionado = (valor = {}) => {

    buscaJobSelecionado.value = valor?.jobCodigo ?? '';

};

const alteraBuscaOSSelecionado = (valor = {}) => {

    buscaOSSelecionado.value = valor?.osCodigo ?? '';

};

const alteraBuscaOSItemItemCodigoSelecionado = (valor = {}) => {

    buscaOSItemItemCodigoSelecionado.value = valor?.osItemItemCodigo ?? '';

};

const alteraBuscaTemFollowUpsSelecionado = (valor = false) => {

    buscaTemFollowUpsSelecionado.value = valor;

};

/*==================================================================*/
/*====================== FILTRA DADOS ==============================*/
/*==================================================================*/

const pedidosDeCompraFiltrados = computed(() => {

    return pedidosDeCompra.value?.reduce((acc, pedido = {}) => {

        const numeroDoPedido = pedido?.codigo ?? '';
        const fornecedor = pedido?.fornecedor?.nome ?? '';
        const temFollowUps = pedido?.temFollowUps ?? false;
        const itens = pedido?.itens ?? [];

        if(buscaNumeroPedidoSelecionado.value !== ''){

            if(!numeroDoPedido.toLowerCase().includes(buscaNumeroPedidoSelecionado.value.toLowerCase())){
                return acc;
            }

        }

        if(buscaFornecedorSelecionado.value !== ''){

            if(!fornecedor.toLowerCase().includes(buscaFornecedorSelecionado.value.toLowerCase())){
                return acc;
            }

        }

        if(buscaTemFollowUpsSelecionado.value){

            if(!temFollowUps){
                return acc;
            }

        }

        if(buscaJobSelecionado.value !== ''){

            const possuiJob = itens.some((item = {}) => {

                const jobCodigo = item?.jobCodigo ?? '';

                return jobCodigo.toLowerCase().includes(buscaJobSelecionado.value.toLowerCase());

            });

            if(!possuiJob){
                return acc;
            }

        }

        if(buscaOSSelecionado.value !== ''){

            const possuiOS = itens.some((item = {}) => {

                const osCodigo = item?.osCodigo ?? '';

                return osCodigo.toLowerCase().includes(buscaOSSelecionado.value.toLowerCase());

            });

            if(!possuiOS){
                return acc;
            }

        }

        if(buscaOSItemItemCodigoSelecionado.value !== ''){

            const possuiCodigoDeItem = itens.some((item = {}) => {

                const osItemItemCodigo = `${item?.osItemItemCodigo ?? ''}`;

                return osItemItemCodigo.toLowerCase().includes(buscaOSItemItemCodigoSelecionado.value.toLowerCase());

            });

            if(!possuiCodigoDeItem){
                return acc;
            }

        }

        acc.push(pedido);

        return acc;

    }, []) ?? [];

});

/*==================================================================*/
/*====================== FILTROS CONFIGURADOS ======================*/
/*==================================================================*/

const filtros = computed(() => [

    { label: 'N° do Pedido', items: buscaNumerosPedidos.value, itemText: 'codigo', onChange: alteraBuscaNumeroPedidoSelecionado },
    { label: 'Fornecedor', items: buscaFornecedores.value, itemText: 'nome', onChange: alteraBuscaFornecedorSelecionado },
    { label: 'Job', items: buscaJobs.value, itemText: 'jobCodigo', onChange: alteraBuscaJobSelecionado },
    { label: 'OS', items: buscaOS.value, itemText: 'osCodigo', onChange: alteraBuscaOSSelecionado },
    { label: 'Código do Item', items: buscaCodigosDeItem.value, itemText: 'osItemItemCodigo', onChange: alteraBuscaOSItemItemCodigoSelecionado },

]);

/*==================================================================*/
/*===================== LOAD PEDIDOS DE COMPRA =====================*/
/*==================================================================*/

const loadPedidosDeCompra = async () => {

    pedidoDeCompraLoading.value = true;

    const pedidosDeCompraResponse = await ERP.getManyWithView(View.PEDIDO_DE_COMPRA_VISUALIZAR_PEDIDOS_DE_COMPRA);
    pedidosDeCompra.value = pedidosDeCompraResponse?.data ?? [];

    pedidoDeCompraLoading.value = false;

};

/*==================================================================*/
/*=================== ACOES DE LINHA ===============================*/
/*==================================================================*/

const aoVisualizarPedidoDeCompra = (item = {}) => {

    alteraVisualizandoPedido({
        visualizandoPedido: true,
        pedidoDeCompra: item,
    });

};

const downloadPDFDoPedido = async (item = {}) => {

    await UtilPDF.downloadPDFFromAction({
        actionName: 'PDF Pedido de Compra',
        documentName: `Pedido de Compra ${item?.codigo ?? ''}`,
        params: {
            id: item?.id,
        },
    });

};

/*==================================================================*/
/*==================== CONTADORES DE APROVACAO =====================*/
/*==================================================================*/

const obtemContadorDeCoordenadores = (aprovadores = []) => {

    const coordenadores = aprovadores.reduce((acc, aprovador = {}) => {

        if(aprovador?.tipo != 'Coordenador'){
            return acc;
        }

        acc.quantidade++;

        if(aprovador?.decisao != ''){
            acc.decidiram++;
        }

        return acc;

    }, { decidiram: 0, quantidade: 0 });

    return `${coordenadores?.decidiram}/${coordenadores?.quantidade}`;

};

const obtemContadorDeDiretores = (aprovadores = []) => {

    const diretores = aprovadores.reduce((acc, aprovador = {}) => {

        if(aprovador?.tipo != 'Diretor'){
            return acc;
        }

        acc.quantidade++;

        if(aprovador?.decisao != ''){
            acc.decidiram++;
        }

        return acc;

    }, { decidiram: 0, quantidade: 0 });

    return `${diretores?.decidiram}/${diretores?.quantidade}`;

};

/*==================================================================*/
/*============================ CICLO DE VIDA =======================*/
/*==================================================================*/

onMounted(async () => {

    await loadPedidosDeCompra();

});

watch(visualizandoPedido, () => {

    if(visualizandoPedido.value == false){

        loadPedidosDeCompra();

    }

});

</script>

<template>

    <TelaTabela
        :disabled="pedidoDeCompraLoading"
        :filtros="filtros"
        :headers="pedidoDeCompraHeaders"
        :dados="pedidosDeCompraFiltrados"
        paginacao="paginada"
    >

        <template #tela-titulo>

            Visualizar Pedidos de Compra

        </template>

        <template #toolbar-baixo-esquerda>

            <CampoCheckboxBase
                :value="buscaTemFollowUpsSelecionado"
                :disabled="pedidoDeCompraLoading"
                @change="alteraBuscaTemFollowUpsSelecionado"
            >

                Só pedidos com FollowUps lançados

            </CampoCheckboxBase>

        </template>

        <template #tabela-linha-botoes="{ dado, disabled }">

            <BotaoIconeBase
                icon="mdi-eye"
                color="#32434d"
                text="Visualizar"
                :disabled="disabled"
                :click="() => aoVisualizarPedidoDeCompra(dado)"
            ></BotaoIconeBase>

            <BotaoIconeBase
                icon="mdi-download"
                color="#3E737B"
                text="Baixar PDF"
                :disabled="disabled"
                :click="() => downloadPDFDoPedido(dado)"
            ></BotaoIconeBase>

        </template>

        <template #tabela-linha-item.itens="{ dado }">

                <v-chip
                    color="primary"
                    label
                >

                    <v-icon left>
                        mdi-cube-outline
                    </v-icon>
                    {{dado?.itens?.length}}

                </v-chip>

        </template>

        <template #tabela-linha-item.parcelas="{ dado }">

                <v-chip
                    color="deep-orange"
                    label
                    dark
                >

                    <v-icon left>
                        mdi-cash-multiple
                    </v-icon>
                    {{dado?.parcelas?.length}}

                </v-chip>

        </template>

        <template #tabela-linha-item.coordenadores="{ dado }">

                <v-chip
                    color="teal"
                    label
                    dark
                >

                    <v-icon left>
                        mdi-account-circle-outline
                    </v-icon>

                    {{obtemContadorDeCoordenadores(dado?.aprovadores)}}

                </v-chip>

        </template>

        <template #tabela-linha-item.diretor="{ dado }">

                <v-chip
                    color="indigo"
                    label
                    dark
                >

                    <v-icon left>
                        mdi-account-circle-outline
                    </v-icon>

                    {{obtemContadorDeDiretores(dado?.aprovadores)}}

                </v-chip>

        </template>

        <template #modais>

            <VisualizarPedidoDeCompraModal
                :usuarioStore="usuarioStore"
                :colaboradorStore="colaboradorStore"
                :pedidoDeCompra="pedidoDeCompra"
                :visualizandoPedido="visualizandoPedido"
                @update:visualizandoPedido="alteraVisualizandoPedido"
            ></VisualizarPedidoDeCompraModal>

        </template>

    </TelaTabela>

</template>